// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Upload, FileText, AlertCircle, ArrowDownLeft, ArrowUpRight, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBRL = (v) =>
  v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Bank statement importer modal (S-27 §55).
 * Accepts CSV / OFX / PDF; sends to backend parser; displays normalized
 * transactions for the auditor to inspect and reconcile against S-26.
 *
 * Nothing is persisted — this is a transient reconciliation helper.
 */
export default function BankStatementImporter({ open, onOpenChange, months }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const handleParse = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/bank-statements/parse`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.warnings?.length) {
        toast.warning(res.data.warnings[0]);
      } else {
        toast.success(`${res.data.transactions.length} transações lidas`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao ler extrato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bank-importer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Importar extrato bancário
          </DialogTitle>
          <DialogDescription>
            S-27 §55 — leia CSV/OFX/PDF do banco para conferir contra o S-26.
            {months?.length > 0 && ` Período em foco: ${months.join(', ')}`}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <Card className="border-dashed">
              <CardContent className="p-6 text-center space-y-3">
                <Upload className="w-10 h-10 text-muted-foreground mx-auto" />
                <Input
                  type="file"
                  accept=".csv,.ofx,.pdf,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="max-w-sm mx-auto"
                  data-testid="bank-file-input"
                />
                {file && (
                  <p className="text-sm text-muted-foreground">
                    Selecionado: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  CSV/OFX são preferíveis (parsing 100% confiável).
                  PDFs são lidos via texto; se o PDF for escaneado, é usado OCR (precisão menor).
                </p>
              </CardContent>
            </Card>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button
                onClick={handleParse}
                disabled={!file || loading}
                data-testid="parse-button"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Processar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 overflow-hidden">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant="outline">Fonte: {result.source_type.toUpperCase()}</Badge>
              <Badge variant="outline">
                Créditos: <span className="ml-1 text-emerald-600 font-semibold">{formatBRL(result.total_credit)}</span>
              </Badge>
              <Badge variant="outline">
                Débitos: <span className="ml-1 text-red-600 font-semibold">{formatBRL(result.total_debit)}</span>
              </Badge>
              {result.detected_balance != null && (
                <Badge variant="outline">Saldo detectado: {formatBRL(result.detected_balance)}</Badge>
              )}
              <span className="flex-1" />
              <Button variant="outline" size="sm" onClick={reset} data-testid="bank-reset-button">
                Outro arquivo
              </Button>
            </div>

            {result.warnings?.map((w, i) => (
              <Card key={i} className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20">
                <CardContent className="p-3 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  {w}
                </CardContent>
              </Card>
            ))}

            <div className="overflow-y-auto border border-border rounded-xl" data-testid="bank-transactions-list">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Data</th>
                    <th className="text-left p-2 font-medium">Descrição</th>
                    <th className="text-right p-2 font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {result.transactions.map((t, i) => (
                    <tr key={`${t.date || 'no-date'}-${i}-${t.amount}`} className="border-t border-border" data-testid={`bank-tx-${i}`}>
                      <td className="p-2 text-muted-foreground">{t.date || '—'}</td>
                      <td className="p-2">{t.description}</td>
                      <td className={`p-2 text-right font-mono ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        <span className="inline-flex items-center justify-end gap-1">
                          {t.amount >= 0
                            ? <ArrowDownLeft className="w-3 h-3" />
                            : <ArrowUpRight className="w-3 h-3" />}
                          {formatBRL(t.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.transactions.length === 0 && (
                <p className="p-6 text-center text-sm text-muted-foreground">Nenhuma transação foi reconhecida.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
